import { type VariantProps, cva } from 'class-variance-authority';
import type { ComponentProps } from 'react';

import { cn } from '../lib/cn';

/**
 * 한 컬럼 화면의 지면 — 3분할 작업대(`Workbench`)가 아닌 화면 전부.
 *
 * 왼쪽은 언제나 거터(`page-inset`)다. 헤더 로고 · 서브내비 첫 항목과 같은 x에서
 * 시작하고, 폭 변형은 그 선에 붙은 최대 폭이다 — 가운데 정렬하지 않는다(D2 §5.2 · D150).
 */
export const pageShellVariants = cva(
  // 페이지 머리(64)가 첫 자식이면 그 높이가 위 여백이다 — 서브내비 바로 아래에 머리가 선다.
  'page-inset flex w-full flex-col pt-8 pb-8 has-[>header:first-child]:pt-0',
  {
    variants: {
      width: {
        /** 단일 폼·온보딩 패널 */
        form: 'page-width-form',
        /** 읽는 지면 */
        read: 'page-width-read',
        /** 편집·검토처럼 옆에 정보가 붙는 지면 */
        wide: 'page-width-wide',
        /** 목록·대시보드 — 셸 끝까지 */
        board: '',
      },
      gap: {
        /** 페이지 머리 바로 아래 작업면(`MasterDetail`)이 붙을 때 */
        none: 'gap-0',
        row: 'gap-row',
        card: 'gap-card',
        pane: 'gap-pane',
        panel: 'gap-panel',
      },
      /** 화면 높이를 채운다 — 세로 가운데 정렬이 필요한 흐름에 쓴다. */
      fill: { true: 'h-full', false: '' },
    },
    defaultVariants: { width: 'board', gap: 'panel', fill: false },
  },
);

export interface PageShellProps
  extends ComponentProps<'div'>, VariantProps<typeof pageShellVariants> {
  as?: 'div' | 'main' | 'section';
}

export function PageShell({
  className,
  width,
  gap,
  fill,
  as: Tag = 'div',
  ...props
}: PageShellProps) {
  return <Tag className={cn(pageShellVariants({ width, gap, fill }), className)} {...props} />;
}
